import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'sonner';
import { ICONS } from '../../icons';
import { submitQuestCompletion, subscribeToUserPendingQuestCompletion } from '../../services/firestoreService';
import { useAuth } from '../../contexts/AuthContext';
import { PageShell, QuestLabel, ResourceLinks } from '../shared';
import MarkdownDisplay from '../shared/MarkdownDisplay';
import { getLocalizedText } from '../../utils/localizationHelpers';
import '../shared/activeQuestProgress.css';

export default function ActiveQuest({ activeQuest, onCompleteStep, userStats }) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [isUploadMode, setIsUploadMode] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [proofSubmissions, setProofSubmissions] = useState({});
  const [sideQuestProofSubmissions, setSideQuestProofSubmissions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pendingCompletion, setPendingCompletion] = useState(null);

  // Subscribe to pending quest completion for the active quest
  useEffect(() => {
    if (!currentUser || !activeQuest?.id) {
      setPendingCompletion(null);
      return;
    }

    const unsubscribe = subscribeToUserPendingQuestCompletion(
      currentUser.uid,
      activeQuest.id,
      (completion) => {
        setPendingCompletion(completion);
      }
    );

    return () => unsubscribe();
  }, [currentUser, activeQuest?.id]);

  const handleCompleteQuest = () => {
    if (activeQuest?.proofRequirements) {
      const initialProofs = {};
      activeQuest.proofRequirements.forEach((proof, index) => {
        initialProofs[index] = {
          value: '',
          file: null,
          label: proof.label,
          inputType: proof.inputType
        };
      });
      setProofSubmissions(initialProofs);
    }

    if (activeQuest?.sideQuests) {
      const initialSideQuestProofs = {};
      activeQuest.sideQuests.forEach((sideQuest, sideQuestIndex) => {
        if (sideQuest.proofRequirements && sideQuest.proofRequirements.length > 0) {
          initialSideQuestProofs[sideQuestIndex] = {};
          sideQuest.proofRequirements.forEach((proof, proofIndex) => {
            initialSideQuestProofs[sideQuestIndex][proofIndex] = {
              value: '',
              file: null,
              label: proof.label,
              inputType: proof.inputType
            };
          });
        }
      });
      setSideQuestProofSubmissions(initialSideQuestProofs);
    }

    setExpandedDescriptions({});
    setIsUploadMode(true);
  };

  const handleCancelUpload = () => {
    setIsUploadMode(false);
    setProofSubmissions({});
    setSideQuestProofSubmissions({});
    setError('');
  };

  const handleRemoveFile = (index) => {
    setProofSubmissions(prev => ({
      ...prev,
      [index]: { ...prev[index], file: null, value: '' }
    }));
  };

  const handleRemoveSideQuestFile = (sideQuestIndex, proofIndex) => {
    setSideQuestProofSubmissions(prev => ({
      ...prev,
      [sideQuestIndex]: {
        ...prev[sideQuestIndex],
        [proofIndex]: { ...prev[sideQuestIndex]?.[proofIndex], file: null, value: '' }
      }
    }));
  };

  const toggleDescription = (key) => {
    setExpandedDescriptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleProofChange = (index, value) => {
    setProofSubmissions(prev => ({ ...prev, [index]: { ...prev[index], value } }));
  };

  const handleProofFileChange = (index, file) => {
    setProofSubmissions(prev => ({
      ...prev,
      [index]: { ...prev[index], file: file || null, value: file ? file.name : '' }
    }));
  };

  const handleSideQuestProofChange = (sideQuestIndex, proofIndex, value) => {
    setSideQuestProofSubmissions(prev => ({
      ...prev,
      [sideQuestIndex]: {
        ...prev[sideQuestIndex],
        [proofIndex]: { ...prev[sideQuestIndex]?.[proofIndex], value }
      }
    }));
  };

  const handleSideQuestProofFileChange = (sideQuestIndex, proofIndex, file) => {
    setSideQuestProofSubmissions(prev => ({
      ...prev,
      [sideQuestIndex]: {
        ...prev[sideQuestIndex],
        [proofIndex]: { ...prev[sideQuestIndex]?.[proofIndex], file: file || null, value: file ? file.name : '' }
      }
    }));
  };

  const handleSubmitCompletion = async () => {
    setSubmitting(true);
    setError('');

    try {
      if (!currentUser) throw new Error('User not authenticated');
      if (!activeQuest) throw new Error('No active quest to complete');

      const mainQuestProofs = Object.values(proofSubmissions).map(p => ({
        value: p.value, label: p.label, inputType: p.inputType, file: p.file || null
      }));

      const sideQuestProofs = {};
      Object.keys(sideQuestProofSubmissions).forEach(sideQuestIndex => {
        sideQuestProofs[sideQuestIndex] = Object.values(sideQuestProofSubmissions[sideQuestIndex]).map(p => ({
          value: p.value, label: p.label, inputType: p.inputType, file: p.file || null
        }));
      });

      await submitQuestCompletion(currentUser.uid, activeQuest.id, activeQuest, mainQuestProofs, sideQuestProofs);

      setIsUploadMode(false);
      setProofSubmissions({});
      setSideQuestProofSubmissions({});

      toast.success('Quest submitted for review!', {
        description: 'Your quest completion has been submitted and is awaiting admin approval.',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error submitting quest completion:', error);
      setError(error.message || 'Failed to submit quest completion. Please try again.');
      toast.error('Failed to submit quest', {
        description: error.message || 'Failed to submit quest completion. Please try again.',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled = () => {
    if (activeQuest?.proofRequirements) {
      const mainQuestMissing = activeQuest.proofRequirements.some((proof, index) => {
        const sub = proofSubmissions[index];
        const type = (proof.inputType || 'link').toLowerCase();
        if (!proof.required) return false;
        if (!sub) return true;
        if (type === 'photo' || type === 'image' || type === 'pdf' || type === 'excel') return !sub.file;
        return !sub.value || sub.value.trim() === '';
      });
      if (mainQuestMissing) return true;
    }

    if (activeQuest?.sideQuests) {
      const sideQuestMissing = activeQuest.sideQuests.some((sideQuest, sideQuestIndex) => {
        if (!sideQuest.proofRequirements) return false;
        const hasStartedSideQuest = sideQuestProofSubmissions[sideQuestIndex] &&
          Object.keys(sideQuestProofSubmissions[sideQuestIndex]).length > 0;
        if (!hasStartedSideQuest) return false;
        return sideQuest.proofRequirements.some((proof, proofIndex) => {
          const sub = sideQuestProofSubmissions[sideQuestIndex]?.[proofIndex];
          const type = (proof.inputType || 'link').toLowerCase();
          if (!proof.required) return false;
          if (!sub) return true;
          if (type === 'photo' || type === 'image' || type === 'pdf' || type === 'excel') return !sub.file;
          return !sub.value || sub.value.trim() === '';
        });
      });
      if (sideQuestMissing) return true;
    }
    return false;
  };

  const getProofTypeIcon = (type) => {
    switch (type) {
      case 'photo': case 'image': return ICONS.IMAGE || ICONS.CAMERA || ICONS.FILE;
      case 'pdf': return ICONS.FILE_PDF || ICONS.FILE;
      case 'excel': return ICONS.FILE_EXCEL || ICONS.FILE;
      default: return ICONS.LINK || ICONS.EXTERNAL_LINK;
    }
  };

  const getFileAccept = (type) => {
    switch (type) {
      case 'photo': case 'image': return 'image/*';
      case 'pdf': return 'application/pdf';
      case 'excel': return '.csv,.xls,.xlsx,text/csv,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      default: return null;
    }
  };

  const isFileType = (type) => ['photo', 'image', 'pdf', 'excel'].includes(type);

  const renderProofInput = (proof, index, submission, onFileChange, onValueChange, onRemoveFile) => {
    const inputType = (proof.inputType || 'link').toLowerCase();
    const hasFile = submission?.file;

    if (isFileType(inputType)) {
      return hasFile ? (
        <div className="cq-file-selected">
          <FontAwesomeIcon icon={getProofTypeIcon(inputType)} className="cq-file-icon" />
          <span className="cq-file-name">{submission.file.name}</span>
          <button type="button" className="cq-file-remove" onClick={onRemoveFile} disabled={submitting}>
            <FontAwesomeIcon icon={ICONS.TIMES} />
          </button>
        </div>
      ) : (
        <input
          type="file"
          accept={getFileAccept(inputType)}
          className="cq-proof-input"
          onChange={(e) => onFileChange(e.target.files?.[0])}
          disabled={submitting}
        />
      );
    }

    return (
      <input
        type="url"
        className="cq-proof-input"
        placeholder="Enter URL"
        value={submission?.value || ''}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={submitting}
      />
    );
  };

  const totalXP = (activeQuest?.value || 0) + (activeQuest?.sideQuests || []).reduce((acc, sq) => acc + (sq.value || 0), 0);
  const totalObjectives = activeQuest ? 1 + (activeQuest.sideQuests?.length || 0) : 0;

  // Empty state
  if (!activeQuest) {
    return (
      <div className="cq-page">
        <div className="cq-empty">
          <div className="cq-empty-glow" />
          <div className="cq-empty-icon">
            <FontAwesomeIcon icon={ICONS.TARGET} />
          </div>
          <h3>{t('quests.active.noActiveQuest')}</h3>
          <p>{t('quests.active.startNewQuest')}</p>
        </div>
      </div>
    );
  }

  return (
    <PageShell className="cq-page">

      {/* Hero Section */}
      <div className="cq-hero">
        {activeQuest.icon && (
          <div className="cq-hero-backdrop" style={{ backgroundImage: `url(${activeQuest.icon})` }} />
        )}
        <div className="cq-hero-content">
          <div className="cq-hero-info">
            <span className="cq-hero-tag">
              <span className="cq-tag-dot" />
              CURRENT MISSION
            </span>
            <h1 className="cq-hero-title">{getLocalizedText(activeQuest.title, 'Active Quest')}</h1>
            <div className="cq-hero-meta">
              <span className="cq-meta-pill cq-meta-xp">
                <FontAwesomeIcon icon={ICONS.COINS} />
                {totalXP} XP
              </span>
              <span className="cq-meta-pill">
                <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
                {totalObjectives} {totalObjectives === 1 ? 'Objective' : 'Objectives'}
              </span>
              {pendingCompletion && !isUploadMode && (
                <span className="cq-meta-pill cq-meta-pending">
                  <FontAwesomeIcon icon={ICONS.CLOCK} />
                  Pending Review
                </span>
              )}
            </div>
          </div>
          <div className="cq-hero-action">
            {isUploadMode ? (
              <div className="cq-upload-actions">
                <button className="cq-btn-cancel" onClick={handleCancelUpload} disabled={submitting}>
                  <FontAwesomeIcon icon={ICONS.TIMES} />
                  <span>Cancel</span>
                </button>
                <button
                  className="cq-btn-submit"
                  onClick={handleSubmitCompletion}
                  disabled={submitting || isSubmitDisabled()}
                >
                  <FontAwesomeIcon icon={submitting ? ICONS.SPINNER : ICONS.CHECK} spin={submitting} />
                  <span>{submitting ? 'Submitting...' : 'Submit'}</span>
                </button>
              </div>
            ) : (
              <button className="cq-btn-complete" onClick={handleCompleteQuest}>
                <div className="cq-btn-complete-inner">
                  <FontAwesomeIcon icon={pendingCompletion ? ICONS.EDIT : ICONS.CHECK} />
                  <span>{pendingCompletion ? 'Revise' : 'Complete'}</span>
                </div>
                <div className="cq-btn-xp">+{totalXP} XP</div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resource Links */}
      <ResourceLinks missionBriefUrl={activeQuest.missionBriefUrl} pressKitUrl={activeQuest.pressKitUrl} />

      {/* Error banner */}
      {isUploadMode && error && (
        <div className="cq-error">
          <FontAwesomeIcon icon={ICONS.EXCLAMATION_TRIANGLE} />
          <span>{error}</span>
        </div>
      )}

      {/* Objectives */}
      <div className="cq-objectives">
        {/* Main Quest */}
        <div className={`cq-objective ${isUploadMode ? 'upload-active' : ''}`}>
          <div className="cq-objective-accent main" />
          <div className="cq-objective-body">
            <div className="cq-objective-header">
              <button className="cq-objective-toggle" onClick={() => toggleDescription('main')} type="button">
                <FontAwesomeIcon
                  icon={ICONS.CHEVRON_DOWN}
                  className={`cq-toggle-chevron ${expandedDescriptions['main'] ? 'expanded' : ''}`}
                />
              </button>
              <div className="cq-objective-info">
                <div className="cq-objective-title-row">
                  <span className="cq-objective-title">Main Quest</span>
                  <QuestLabel type="xp" value={activeQuest.value || 0} size="small" color="neutral" />
                </div>
                {/* Proof tags */}
                {activeQuest.proofRequirements && activeQuest.proofRequirements.length > 0 && (
                  <div className="cq-proof-tags">
                    {activeQuest.proofRequirements.map((proof, index) => (
                      <span key={index} className={`cq-proof-tag ${proof.required ? 'required' : ''}`}>
                        <FontAwesomeIcon icon={getProofTypeIcon((proof.inputType || 'link').toLowerCase())} />
                        <span>{getLocalizedText(proof.label, `Proof ${index + 1}`)}</span>
                        {proof.required && <span className="cq-required-dot" />}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Expandable description */}
            <div className={`cq-objective-desc-wrap ${expandedDescriptions['main'] ? 'expanded' : ''}`}>
              <div className="cq-objective-desc-overflow">
                <div className="cq-objective-desc">
                  <MarkdownDisplay content={getLocalizedText(activeQuest.description, 'Complete the main objective')} />
                </div>
              </div>
            </div>

            {/* Upload inputs */}
            {isUploadMode && activeQuest?.proofRequirements?.length > 0 && (
              <div className="cq-upload-section">
                {activeQuest.proofRequirements.map((proof, index) => (
                  <div key={index} className="cq-upload-item">
                    <label className="cq-upload-label">
                      {getLocalizedText(proof.label, `Proof ${index + 1}`)}
                      {proof.required && <span className="cq-required">*</span>}
                    </label>
                    {renderProofInput(
                      proof, index, proofSubmissions[index],
                      (file) => handleProofFileChange(index, file),
                      (val) => handleProofChange(index, val),
                      () => handleRemoveFile(index)
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Side Quests */}
        {activeQuest.sideQuests && activeQuest.sideQuests.length > 0 && (
          <>
            <div className="cq-divider">
              <div className="cq-divider-line" />
              <span className="cq-divider-label">Side Quests</span>
              <div className="cq-divider-line" />
            </div>

            {activeQuest.sideQuests.map((sideQuest, index) => (
              <div key={index} className={`cq-objective ${isUploadMode ? 'upload-active' : ''}`}>
                <div className="cq-objective-accent side" />
                <div className="cq-objective-body">
                  <div className="cq-objective-header">
                    <button className="cq-objective-toggle" onClick={() => toggleDescription(`side-${index}`)} type="button">
                      <FontAwesomeIcon
                        icon={ICONS.CHEVRON_DOWN}
                        className={`cq-toggle-chevron ${expandedDescriptions[`side-${index}`] ? 'expanded' : ''}`}
                      />
                    </button>
                    <div className="cq-objective-info">
                      <div className="cq-objective-title-row">
                        <span className="cq-objective-title">{getLocalizedText(sideQuest.title, `Side Quest ${index + 1}`)}</span>
                        <QuestLabel type="xp" value={sideQuest.value || 0} size="small" color="neutral" />
                      </div>
                      {sideQuest.proofRequirements && sideQuest.proofRequirements.length > 0 && (
                        <div className="cq-proof-tags">
                          {sideQuest.proofRequirements.map((proof, proofIndex) => (
                            <span key={proofIndex} className={`cq-proof-tag ${proof.required ? 'required' : ''}`}>
                              <FontAwesomeIcon icon={getProofTypeIcon((proof.inputType || 'link').toLowerCase())} />
                              <span>{getLocalizedText(proof.label, `Proof ${proofIndex + 1}`)}</span>
                              {proof.required && <span className="cq-required-dot" />}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`cq-objective-desc-wrap ${expandedDescriptions[`side-${index}`] ? 'expanded' : ''}`}>
                    <div className="cq-objective-desc-overflow">
                      <div className="cq-objective-desc">
                        <MarkdownDisplay content={getLocalizedText(sideQuest.description, 'Complete this optional objective')} />
                      </div>
                    </div>
                  </div>

                  {isUploadMode && sideQuest.proofRequirements?.length > 0 && (
                    <div className="cq-upload-section">
                      {sideQuest.proofRequirements.map((proof, proofIndex) => (
                        <div key={proofIndex} className="cq-upload-item">
                          <label className="cq-upload-label">
                            {getLocalizedText(proof.label, `Proof ${proofIndex + 1}`)}
                            {proof.required && <span className="cq-required">*</span>}
                          </label>
                          {renderProofInput(
                            proof, proofIndex, sideQuestProofSubmissions[index]?.[proofIndex],
                            (file) => handleSideQuestProofFileChange(index, proofIndex, file),
                            (val) => handleSideQuestProofChange(index, proofIndex, val),
                            () => handleRemoveSideQuestFile(index, proofIndex)
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </PageShell>
  );
}
